import React, { useState } from "react";
import { Search, ShoppingBag, User, MapPin, LogOut } from "lucide-react";
import { SHOP } from "@/constants/testIds";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

export default function Header({ onLoginClick, onSearch }) {
  const { totalQty, setOpen } = useCart();
  const { user, pendingUser, setProfileOpen, logout, openLogin } = useAuth();
  const [query, setQuery] = useState("");

  const submitSearch = (e) => {
    e.preventDefault();
    onSearch?.(query.trim());
  };

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 sm:gap-6 h-16 sm:h-20">
          {/* Logo */}
          <a href="/" data-testid={SHOP.logo} className="flex items-center gap-2 shrink-0 group">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/0c393ec1-3405-4eb1-be58-a2051533df5b/images/fb222513f0229b28878ce0533de4e321210e334f5bf1c00ab4d490f4f96d70dc.png"
              alt="Adeshwar Electricals logo"
              className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg object-contain shadow-sm"
            />
            <div className="leading-none">
              <div className="font-display font-extrabold text-base sm:text-lg text-gray-900 tracking-tight">
                Adeshwar
              </div>
              <div className="text-[10px] sm:text-[11px] font-bold tracking-[0.18em] text-[#0056D2] uppercase">
                Electricals
              </div>
            </div>
          </a>

          {/* Location pill (desktop) */}
          <button className="hidden md:flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-left">
            <MapPin size={16} className="text-[#FF5722]" />
            <div className="leading-tight">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Deliver to</div>
              <div className="text-xs font-bold text-gray-900">Bangalore 560053</div>
            </div>
          </button>

          {/* Search */}
          <form onSubmit={submitSearch} className="flex-1 min-w-0">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                data-testid={SHOP.searchInput}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search wires, switches, LED bulbs..."
                className="w-full h-10 sm:h-11 pl-10 pr-4 rounded-lg bg-gray-100 border border-transparent focus:bg-white focus:border-[#0056D2] focus:ring-2 focus:ring-[#0056D2]/20 outline-none text-sm placeholder:text-gray-500 transition-all"
              />
            </div>
          </form>

          {/* Actions */}
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            {user ? (
              <div className="flex items-center gap-1">
                <div data-testid="header-user-chip" className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 text-sm font-semibold text-gray-900 max-w-[160px]">
                  <User size={16} className="text-[#0056D2] shrink-0" />
                  <span className="truncate">{user.name}</span>
                </div>
                <button
                  data-testid="header-logout-btn"
                  onClick={logout}
                  title="Logout"
                  className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg hover:bg-gray-100 transition-colors text-xs font-bold text-gray-600"
                >
                  <LogOut size={16} />
                  <span className="hidden sm:inline">Logout</span>
                </button>
              </div>
            ) : pendingUser ? (
              <div className="flex items-center gap-1">
                <button
                  data-testid="header-pending-chip"
                  onClick={() => setProfileOpen(true)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-100 text-amber-800 text-xs font-bold"
                >
                  <User size={14} />
                  <span className="hidden sm:inline">Awaiting approval</span>
                  <span className="sm:hidden">Pending</span>
                </button>
                <button
                  data-testid="header-logout-btn"
                  onClick={logout}
                  title="Logout"
                  className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg hover:bg-gray-100 transition-colors text-xs font-bold text-gray-600"
                >
                  <LogOut size={16} />
                </button>
              </div>
            ) : (
              <button
                data-testid={SHOP.loginBtn}
                onClick={onLoginClick}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm font-semibold text-gray-900"
              >
                <User size={18} />
                <span>Login</span>
              </button>
            )}

            <button
              data-testid={SHOP.cartBtn}
              onClick={() => (user ? setOpen(true) : openLogin())}
              className="relative flex items-center gap-2 px-3 py-2 rounded-lg bg-[#FFD700] hover:bg-[#FFC107] transition-colors text-gray-900 font-bold text-sm"
            >
              <ShoppingBag size={18} />
              <span className="hidden sm:inline">Cart</span>
              {totalQty > 0 && (
                <span
                  data-testid={SHOP.cartCount}
                  className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-[#FF5722] text-white text-[10px] font-bold grid place-items-center border-2 border-white"
                >
                  {totalQty}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
